// src/containers/preview/demoContent.js
//
// Everything the public home-page demo shows that ISN'T a real person.
//
//   DEMO_FALLBACK_TOPICS  the six sample topics, used until an admin publishes
//                         publicDemo/home from Layout Builder ▸ 🏠 Home page
//                         demo. Media URLs and lyrics come from
//                         preview-topics.js (unchanged); questions come from
//                         the static quiz library on first play.
//   DEMO_CLASS            made-up students (names, avatar looks, how strong
//                         they are). Their progress, scores and answers are
//                         generated from the REAL demo questions, so the
//                         "teacher view" shows real SBL questions.
//   DEMO_TEACHER          the made-up teacher whose class this is.
//
// Nothing here is read from or written to anyone's account.

import previewTopics from "./preview-topics";

export const DEMO_TEACHER = { name: "Ms. Rivera", room: "Room 12 Rockets" };

// Subject labels / icons in the same wording the portal uses.
export const DEMO_SUBJECTS = {
  science: { label: "Science", icon: "🔬" },
  socialstudies: { label: "Social Studies", icon: "🌎" },
  math: { label: "Math", icon: "🔢" },
};

// Extra facts for the six topics already in preview-topics.js, matched by
// title. `quizSlug` is the static quiz-library entry used when Firestore
// hasn't supplied questions (the published demo document usually does).
const EXTRA = {
  "10 Times Tables": {
    slug: "10timestable",
    quizSlug: "10timestable",
    subject: "math",
    grade: "3rd",
    standardNumber: "3.OA.7",
    description: "Fluently multiply and divide within 100 — the 10s facts.",
    bonusRoundType: "timesTable:10",
  },
  "Our Flag": {
    slug: "americanflag",
    quizSlug: "americanflagmc",
    subject: "socialstudies",
    grade: "1st",
    standardNumber: "K.2",
    description: "Recognize national symbols such as the American flag — its stars, stripes and what they stand for.",
  },
  Landforms: {
    slug: "landforms",
    quizSlug: "",
    subject: "science",
    grade: "2nd",
    standardNumber: "2-ESS2-2",
    description: "Develop a model to represent the shapes and kinds of land and bodies of water in an area.",
  },
  "English Ruler": {
    slug: "englishruler",
    quizSlug: "englishruler",
    subject: "math",
    grade: "2nd",
    standardNumber: "2.MD.1",
    description: "Measure the length of an object using rulers, yardsticks and measuring tapes.",
  },
  "As a Native": {
    slug: "asanative",
    quizSlug: "asanative",
    subject: "socialstudies",
    grade: "5th",
    standardNumber: "5.1.1",
    description: "How geography and climate shaped the way Native nations lived — food, shelter, clothing and tools.",
  },
  "Adapt to Survive": {
    slug: "adapttosurvive",
    quizSlug: "adaptation",
    subject: "science",
    grade: "3rd",
    standardNumber: "3-LS4-2",
    description: "How differences in body parts and behavior help animals survive, find mates and reproduce.",
  },
};

export const DEMO_FALLBACK_TOPICS = previewTopics.slice(0, 6).map((t) => {
  const x = EXTRA[t.title] || {};
  return {
    slug: x.slug || String(t.title || "").toLowerCase().replace(/[^a-z0-9]/g, ""),
    quizSlug: x.quizSlug || "",
    title: t.title,
    subject: x.subject || "science",
    grade: x.grade || "",
    standardNumber: x.standardNumber || "",
    description: x.description || "",
    imageUrl: "",
    videoUrl: String(t.video || "").trim(),
    musicUrl: String(t.music || "").trim(),
    singAlongUrl: "",
    lyrics: Array.isArray(t.lyrics) ? t.lyrics : [],
    sync: null,
    gameType: "",
    bonusRoundType: x.bonusRoundType || "diamondTap",
    questions: [],
  };
});

/* ------------------------------------------------------------------ */
/* Made-up class                                                       */
/* ------------------------------------------------------------------ */

// `skill` is the chance of getting a question right; `effort` is the chance
// of having watched / listened to a topic. Looks use only built-in avatar
// parts (avatarCatalog.json) so every face draws without a network call.
export const DEMO_CLASS = [
  {
    id: "demo-maya",
    first: "Maya",
    last: "J.",
    skill: 0.95,
    effort: 0.95,
    coins: 412,
    look: { v: 2, skin: 7, hair: "puff", hairColor: "av-hc-black", eyes: 1, eyeColor: 0, mouth: 1, top: "av-top-varsity", topColor: "av-col-purple", glasses: "", pin: "av-pin-star", bg: 4, blush: true },
  },
  {
    id: "demo-leo",
    first: "Leo",
    last: "M.",
    skill: 0.85,
    effort: 0.85,
    coins: 356,
    look: { v: 2, skin: 2, hair: "messy", hairColor: "av-hc-brown", eyes: 0, eyeColor: 3, mouth: 2, top: "av-top-jersey", topColor: "av-col-red", glasses: "av-gl-03", pin: "", bg: 0, blush: true, freckles: true },
  },
  {
    id: "demo-aiko",
    first: "Aiko",
    last: "T.",
    skill: 0.9,
    effort: 0.75,
    coins: 331,
    look: { v: 2, skin: 1, hair: "bob", hairColor: "av-hc-black", eyes: 3, eyeColor: 0, mouth: 0, top: "av-top-sweater", topColor: "av-col-pink", glasses: "", pin: "av-pin-heart", bg: 2, blush: true },
  },
  {
    id: "demo-jayden",
    first: "Jayden",
    last: "W.",
    skill: 0.75,
    effort: 0.9,
    coins: 298,
    look: { v: 2, skin: 8, hair: "hightop", hairColor: "av-hc-black", eyes: 2, eyeColor: 1, mouth: 1, top: "av-top-hoodie", topColor: "av-col-green", glasses: "", pin: "av-pin-rocket", bg: 3, blush: false },
  },
  {
    id: "demo-sofia",
    first: "Sofia",
    last: "R.",
    skill: 0.8,
    effort: 0.7,
    coins: 254,
    look: { v: 2, skin: 4, hair: "ponytail", hairColor: "av-hc-darkbrown", eyes: 1, eyeColor: 2, mouth: 3, top: "av-top-polo", topColor: "av-col-teal", glasses: "", pin: "av-pin-flower", bg: 5, blush: true },
  },
  {
    id: "demo-kai",
    first: "Kai",
    last: "B.",
    skill: 0.65,
    effort: 0.6,
    coins: 187,
    look: { v: 2, skin: 6, hair: "longtwists", hairColor: "av-hc-darkbrown", eyes: 0, eyeColor: 0, mouth: 4, top: "av-top-jacket", topColor: "av-col-navy", glasses: "", pin: "", bg: 6, blush: true },
  },
  {
    id: "demo-emma",
    first: "Emma",
    last: "K.",
    skill: 0.6,
    effort: 0.55,
    coins: 142,
    look: { v: 2, skin: 0, hair: "pigtails", hairColor: "av-hc-blonde", eyes: 3, eyeColor: 4, mouth: 1, top: "av-top-tee", topColor: "av-col-yellow", glasses: "av-gl-01", pin: "", bg: 1, blush: true, freckles: true },
  },
  {
    id: "demo-noah",
    first: "Noah",
    last: "S.",
    skill: 0.5,
    effort: 0.4,
    coins: 96,
    look: { v: 2, skin: 5, hair: "lowfade", hairColor: "av-hc-black", eyes: 0, eyeColor: 0, mouth: 0, top: "av-top-zip", topColor: "av-col-gray", glasses: "", pin: "", bg: 9, blush: false },
  },
];

/* ------------------------------------------------------------------ */
/* Seeded "random" so a student's answers are the same on every open   */
/* ------------------------------------------------------------------ */

const hash = (s = "") => {
  let h = 2166136261;
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
};

const rng = (seed) => {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

/**
 * One question as the teacher's answer list shows it:
 * { question, selectedText, correctText, correct } — the same shape the real
 * game stores in userData…game.latestDetail.answers.
 * Returns an ARRAY because a drag-and-drop / matching set is one row per pair.
 */
const answerRows = (q, rand, skill) => {
  if (Array.isArray(q?.answers) && q.answers.length) {
    const right = q.answers.find((a) => a.correct) || q.answers[0];
    const wrong = q.answers.filter((a) => a !== right);
    const ok = rand() < skill || !wrong.length;
    const picked = ok ? right : wrong[Math.floor(rand() * wrong.length)];
    return [
      {
        question: q.question || q.prompt || "Question",
        selectedText: String(picked?.text ?? ""),
        correctText: String(right?.text ?? ""),
        correct: ok,
      },
    ];
  }

  const pairs = Array.isArray(q?.options)
    ? q.options.map((o) => ({ left: o.text, right: o.targetText }))
    : Array.isArray(q?.pairs)
    ? q.pairs.map((p) => ({ left: p.left, right: p.right }))
    : [];

  if (pairs.length) {
    return pairs.map((p, i) => {
      const ok = rand() < skill || pairs.length < 2;
      const other = pairs[(i + 1) % pairs.length];
      return {
        question: `Match: ${p.right}`,
        selectedText: String(ok ? p.left : other.left),
        correctText: String(p.left),
        correct: ok,
      };
    });
  }

  return [];
};

/**
 * Everything the teacher's "View Students" screen shows for one made-up
 * student, built from the demo topics actually on the page.
 */
export const studentReport = (student, topics = []) => {
  const rows = topics.map((topic) => {
    const rand = rng(hash(`${student.id}:${topic.slug}`));
    const video = Boolean(topic.videoUrl) && rand() < student.effort;
    const music = Boolean(topic.musicUrl) && rand() < student.effort;
    const singalong = Boolean(topic.singAlongUrl) && rand() < student.effort * 0.8;
    const played = (topic.questions || []).length > 0 && rand() < Math.min(1, student.effort + 0.15);

    const answers = played ? (topic.questions || []).slice(0, 8).flatMap((q) => answerRows(q, rand, student.skill)) : [];
    const right = answers.filter((a) => a.correct).length;
    const quizScore = right * 10;
    const bonusScore = played ? Math.round(20 + rand() * 60 * student.skill) : 0;
    const plays = played ? 1 + Math.floor(rand() * 3) : 0;

    return {
      slug: topic.slug,
      title: topic.title,
      standardNumber: topic.standardNumber || "",
      video,
      music,
      singalong,
      game: played,
      plays,
      quizScore,
      bonusScore,
      score: played ? quizScore + bonusScore : 0,
      right,
      total: answers.length,
      answers,
    };
  });

  return {
    rows,
    videoComplete: rows.filter((r) => r.video).length,
    musicComplete: rows.filter((r) => r.music).length,
    gameComplete: rows.filter((r) => r.game).length,
    totalScore: rows.reduce((n, r) => n + r.score, 0),
    totalTopics: rows.length,
  };
};

/** Weekly class board: the made-up class plus "You" with this visit's coins. */
export const demoBoard = (myCoins = 0) => {
  const rows = DEMO_CLASS.map((s) => ({ id: s.id, name: `${s.first} ${s.last}`, coins: s.coins, look: s.look }));
  if (myCoins > 0) rows.push({ id: "me", name: "You", coins: myCoins, look: null, me: true });
  rows.sort((a, b) => b.coins - a.coins);
  return rows.map((r, i) => ({ ...r, place: i + 1 }));
};

/** "3d 4h" until next Monday — the same reset the real board shows. */
export const weekResetLabel = () => {
  const now = new Date();
  const next = new Date(now);
  next.setHours(0, 0, 0, 0);
  next.setDate(next.getDate() + ((8 - next.getDay()) % 7 || 7));
  const ms = next - now;
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  return d > 0 ? `${d}d ${h}h` : `${h}h`;
};

/** Normalises a finished game's payload into answer rows (same as the real player). */
export const answersFromPayload = (payload) => {
  if (Array.isArray(payload?.answers)) return payload.answers;
  const details = payload?.details || [];
  return details.flatMap((round) =>
    (round?.pairs || []).map((p) => ({
      question: round?.question ? `${round.question} — ${p.targetLabel}` : `Match: ${p.targetLabel}`,
      selectedText: p.studentLabel || "(no answer)",
      correctText: p.correctLabel || "(unknown)",
      correct: !!p.isCorrect,
    }))
  );
};
